/**
 * RelInteractionsTab - Direct interaction evidence between two people.
 * For MVP, shows same data as co-mentions filtered by interaction indicators.
 */

import React from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import useRelationshipDetail from '../../hooks/useRelationshipDetail';
import EvidenceChunks from './EvidenceChunks';

function RelInteractionsTab({ sourceNode, targetNode, entityId }) {
  const { interactions, loading, error, retry } = useRelationshipDetail(
    entityId,
    sourceNode.id,
    targetNode.id
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={retry}>Retry</Button>}
        >
          Failed to load interactions
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Direct Interactions ({interactions.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Evidence of direct exchanges between {sourceNode.label} and {targetNode.label}
      </Typography>

      {interactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No direct interaction evidence found.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Direct interactions are identified from conversation transcripts where both people
            are active participants.
          </Typography>
        </Box>
      ) : (
        <EvidenceChunks chunks={interactions} />
      )}
    </Box>
  );
}

export default RelInteractionsTab;
